import {
  STRIPE_PAYMENT_INSTRUMENT_TYPE_CARD,
  stripePaymentInstrumentApiType,
} from 'client-portal/utils/stripe-payment-instrument';
import { buildValidations, validator } from 'ember-cp-validations';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed, set } from '@ember/object';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import { observes } from '@ember-decorators/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import BillingItemMixin from 'client-portal/mixins/polymorphic/billing-item';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import generateUUID from 'ember-simplepractice/utils/generate-uuid';

const Validations = buildValidations({
  amount: [
    validator('number', {
      lte: reads('model.maximumAmount'),
      descriptionKey: 'errors.payment.amount',
      messageKey: computed('model._invoice', function () {
        if (this.get('model._invoice')) {
          return 'errors.payment.amountGreaterThanInvoice';
        } else {
          return 'errors.payment.amountGreaterThanBalance';
        }
      }),
    }),
    validator('number', {
      gt: 0.5,
      allowNone: false,
      descriptionKey: 'errors.payment.amount',
      messageKey: 'errors.payment.amountTooSmall',
    }),
  ],
});

export default class PaymentModel extends Model.extend(BillingItemMixin, Validations) {
  @service stripe;

  @belongsTo('invoice', { async: false, inverse: null }) invoice;
  @belongsTo('appointment-client', { async: false, inverse: null }) appointmentClient;
  @hasMany('invoice-item', { async: false, inverse: null }) invoiceItems;
  @attr() displayName;
  @attr('moment') paidAt;
  @attr() amount;
  @attr() maximumAmount;
  @attr() amountType;

  @belongsTo('polymorphic/stripe-payment-instrument', {
    polymorphic: true,
    async: false,
    inverse: null,
  })
  stripePaymentInstrument;

  get stripePaymentInstrumentType() {
    return stripePaymentInstrumentApiType(this.stripePaymentInstrument);
  }

  // Keep as a classic @computed so `payment-modal`'s
  // `@alias('model.card.isNew')` propagates when `stripePaymentInstrument`
  // changes.
  @computed('stripePaymentInstrument')
  get card() {
    if (this.stripePaymentInstrumentType === STRIPE_PAYMENT_INSTRUMENT_TYPE_CARD) {
      return this.stripePaymentInstrument;
    }

    return null;
  }

  set card(value) {
    if (value?.constructor?.modelName === 'card') {
      set(this, 'stripePaymentInstrument', value);
    } else if (
      value === null &&
      this.stripePaymentInstrumentType === STRIPE_PAYMENT_INSTRUMENT_TYPE_CARD
    ) {
      set(this, 'stripePaymentInstrument', null);
    }
  }

  // Virtual
  @attr() idempotencyKey;
  @attr() customCardToken;
  @attr() radarSession;
  @reads('paidAt') activityDate;
  @reads('amount') activityPayments;
  activityCharges = null;

  // eslint-disable-next-line ember/no-observers
  @observes('stripePaymentInstrument', 'customCardToken')
  paymentInstrumentChanged() {
    if (this.isDeleted) {
      return;
    }
    this.idempotencyKey = generateUUID();
  }

  createRadarSession() {
    return new Promise((resolve, _) => {
      if (isEmberTesting()) {
        return resolve(this);
      }

      // eslint-disable-next-line promise/prefer-await-to-then
      return this.stripe.createRadarSession().then(result => {
        if (result.error) {
          resolve(this);
          return;
        }

        this.radarSession = result.radarSession.id;
        resolve(this);
      });
    });
  }
}
